import { TableCell, TableRow, Text } from '@/src/ui-components';
import { BookOffer } from 'xrpl';
import { useOfferValues } from '../hooks';

type SpreadSeparatorProps = {
  topSellOffer?: BookOffer;
  topBuyOffer?: BookOffer;
};

/**
 * Component to display the spread between buy and sell orders
 * @param topSellOffer The lowest sell offer (best ask)
 * @param topBuyOffer The highest buy offer (best bid)
 * @returns React.ReactNode
 */
export const SpreadSeparator = ({ topSellOffer, topBuyOffer }: SpreadSeparatorProps) => {
  if (!topSellOffer || !topBuyOffer) {
    return (
      <TableRow className="border-t-2 border-b-2 border-muted">
        <TableCell colSpan={3} className="p-2 text-center">
          <Text size="sm" className="text-muted-foreground font-mono">
            No spread data
          </Text>
        </TableCell>
      </TableRow>
    );
  }

  return <SpreadSeparatorContent topSellOffer={topSellOffer} topBuyOffer={topBuyOffer} />;
};

/**
 * Internal component that safely uses hooks for spread calculation
 */
const SpreadSeparatorContent = ({
  topSellOffer,
  topBuyOffer,
}: Required<SpreadSeparatorProps>) => {
  const sellValues = useOfferValues(topSellOffer, 'sell');
  const buyValues = useOfferValues(topBuyOffer, 'buy');

  const spread = Number(sellValues.price) - Number(buyValues.price);
  const midPrice = (Number(sellValues.price) + Number(buyValues.price)) / 2;
  const spreadPercent = ((spread / midPrice) * 100).toFixed(2);

  return (
    <TableRow className="border-t-2 border-b-2 border-muted bg-muted/30">
      <TableCell colSpan={3} className="p-2">
        <div className="flex justify-between items-center font-mono text-xs">
          <Text size="sm" className="text-muted-foreground">
            Spread: <span className="font-semibold">{spread.toFixed(4)}</span> ({spreadPercent}%)
          </Text>
          <Text size="sm" className="text-muted-foreground">
            Mid: <span className="font-semibold">{midPrice.toFixed(4)}</span>
          </Text>
        </div>
      </TableCell>
    </TableRow>
  );
};
